import React, { useEffect, useState } from "react";
import {
  Card,
  Header,
  ProgressFill,
  ProgressLabel,
  ProgressTrack,
  ProgressWrapper,
  Subtitle,
  TextGroup,
  Title,
  Wrapper,
} from "./styles";

import { AiOutlineCloseCircle } from "react-icons/ai";

import { FiCheckCircle, FiAlertCircle } from "react-icons/fi";

import DotLoader from "react-spinners/ClipLoader";
import { ImportJob } from "@domain/models/ImportJob";
import { RemoveJob } from "@domain/models/RemoveJob";

interface Props {
  job: ImportJob | RemoveJob | null;
  type: "import" | "remove";
  onClose: () => void;
}

export const JobProgressBar = ({ job, type, onClose }: Props) => {
  const [closing, setClosing] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (job) {
      setVisible(true);
      setClosing(false);
    }
  }, [job]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      onClose();
    }, 300);
  };

  const getMessages = () => {
    if (type === "import") {
      if (job.type === "undo") {
        return {
          action: "importação",
          actionTitle: "Importação",
          actionInProgress: "Importando",
        };
      } else {
        return {
          action: "reversão",
          actionTitle: "Reversão",
          actionInProgress: "Revertendo",
        };
      }
    } else {
      if (job.type === "undo") {
        return {
          action: "reversão",
          actionTitle: "Reversão",
          actionInProgress: "Revertendo",
        };
      } else {
        return {
          action: "remoção",
          actionTitle: "Remoção",
          actionInProgress: "Removendo",
        };
      }
    }
  };

  const getTitle = (status: ImportJob["status"] | RemoveJob["status"]): string => {
    const { action, actionTitle, actionInProgress } = getMessages();

    switch (status) {
      case "pending":
        return `Preparando ${action}...`;
      case "processing":
        return job.progress === 100
          ? `Finalizando ${action}...`
          : `${actionInProgress} clientes...`;
      case "completed":
        return `${actionTitle} concluída!`;
      case "failed":
        return `Preparando ${action}...`;
    }
  };

  const getSubtitle = (job: ImportJob | RemoveJob): string => {
    if (job.status === "completed" && job.result?.message) {
      return job.result.message;
    }
    if (job.status === "failed") {
      return job.error ? job.error : "Ocorreu um erro inesperado.";
    }
    if (job.status === "processing") {
      return `${job.processedClients} de ${job.totalClients} clientes`;
    }
    return "Aguarde...";
  };

  if (!job || !visible) return null;

  const isDone = job.status === "completed" || job.status === "failed";

  return (
    <Wrapper visible={visible} closing={closing}>
      <Card>
        <Header>
          {job.status === "completed" && <FiCheckCircle color="var(--aqua)" />}
          {job.status === "failed" && <FiAlertCircle color={"var(--red)"} />}
          {(job.status === "pending" || job.status === "processing") && (
            <DotLoader size={18} color={"var(--red)"} loading={true} speedMultiplier={1.5} />
          )}

          <TextGroup>
            <Title>{getTitle(job.status)}</Title>
            <Subtitle>{getSubtitle(job)}</Subtitle>
          </TextGroup>

          {isDone && (
            <AiOutlineCloseCircle
              onClick={handleClose}
              strokeWidth={0.5}
              cursor="pointer"
              size={22}
            />
          )}
        </Header>

        {!isDone && (
          <ProgressWrapper>
            <ProgressTrack>
              <ProgressFill progress={job.progress} status={job.status} />
            </ProgressTrack>
            <ProgressLabel>{job.progress}%</ProgressLabel>
          </ProgressWrapper>
        )}
      </Card>
    </Wrapper>
  );
};
